import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { LANGUAGES, PRESET_CATEGORIES } from "@/lib/api";

const CUSTOM = "__custom__";

export const CreateChannelDialog = ({ open, onOpenChange, onSubmit }) => {
  const [name, setName] = useState("");
  const [language, setLanguage] = useState("tr");
  const [category, setCategory] = useState(PRESET_CATEGORIES[0]);
  const [customCategory, setCustomCategory] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setName("");
      setLanguage("tr");
      setCategory(PRESET_CATEGORIES[0]);
      setCustomCategory("");
    }
  }, [open]);

  const submit = async () => {
    const finalCategory = category === CUSTOM ? customCategory.trim() : category;
    if (!name.trim() || !finalCategory) return;
    setSubmitting(true);
    try {
      await onSubmit({ name: name.trim(), language, category: finalCategory });
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="create-channel-dialog"
        className="bg-[#0F0F0F] border border-border rounded-none max-w-md p-0 gap-0 shadow-none"
      >
        <DialogHeader className="p-6 border-b border-border">
          <span className="label-mono text-primary mb-1">// new_channel</span>
          <DialogTitle className="heading-font font-black text-2xl tracking-tight text-white">
            Yeni Kanal Oluştur
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground mt-1">
            Kanal adı, dili ve kategorisiyle yeni bir üretim alanı aç.
          </DialogDescription>
        </DialogHeader>

        <div className="p-6 space-y-5">
          <div className="space-y-2">
            <Label className="label-mono text-muted-foreground">Kanal Adı</Label>
            <Input
              data-testid="channel-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Örn. Kanal Zero"
              className="bg-background border-border rounded-none h-11 focus-visible:ring-primary focus-visible:ring-1 focus-visible:ring-offset-0"
            />
          </div>

          <div className="space-y-2">
            <Label className="label-mono text-muted-foreground">Dil</Label>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger
                data-testid="channel-language-trigger"
                className="bg-background border-border rounded-none h-11 focus:ring-primary focus:ring-1 focus:ring-offset-0"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0F0F0F] border-border rounded-none">
                {LANGUAGES.map((l) => (
                  <SelectItem
                    key={l.code}
                    value={l.code}
                    data-testid={`language-option-${l.code}`}
                    className="rounded-none font-body focus:bg-[#1A1A1A]"
                  >
                    <span className="font-mono text-xs mr-3 text-muted-foreground">
                      {l.code.toUpperCase()}
                    </span>
                    {l.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="label-mono text-muted-foreground">Kategori</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger
                data-testid="channel-category-trigger"
                className="bg-background border-border rounded-none h-11 focus:ring-primary focus:ring-1 focus:ring-offset-0"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0F0F0F] border-border rounded-none max-h-72">
                {PRESET_CATEGORIES.map((c) => (
                  <SelectItem
                    key={c}
                    value={c}
                    data-testid={`category-option-${c}`}
                    className="rounded-none font-body focus:bg-[#1A1A1A]"
                  >
                    {c}
                  </SelectItem>
                ))}
                <SelectItem
                  value={CUSTOM}
                  data-testid="category-option-custom"
                  className="rounded-none font-body focus:bg-[#1A1A1A] text-primary"
                >
                  + Özel kategori ekle…
                </SelectItem>
              </SelectContent>
            </Select>
            {category === CUSTOM && (
              <Input
                data-testid="channel-custom-category-input"
                value={customCategory}
                onChange={(e) => setCustomCategory(e.target.value)}
                placeholder="Özel kategori adı"
                className="bg-background border-border rounded-none h-11 mt-2 focus-visible:ring-primary focus-visible:ring-1 focus-visible:ring-offset-0"
              />
            )}
          </div>
        </div>

        <DialogFooter className="p-4 border-t border-border gap-2">
          <Button
            data-testid="channel-cancel-btn"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="rounded-none h-10 text-muted-foreground hover:text-white hover:bg-[#1A1A1A]"
          >
            Vazgeç
          </Button>
          <Button
            data-testid="channel-submit-btn"
            onClick={submit}
            disabled={
              submitting ||
              !name.trim() ||
              (category === CUSTOM ? !customCategory.trim() : !category)
            }
            className="rounded-none h-10 bg-primary hover:bg-[#D63028] text-white font-bold uppercase tracking-wider text-xs transition-colors"
          >
            {submitting ? "Oluşturuluyor…" : "Kanalı Oluştur"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateChannelDialog;
